import {
  Box,
  Container,
  Card,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  IconButton,
  Skeleton,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import * as React from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { generateSiteTableData } from '../../utils/apiCommonMethods';
import SiteStatusCharts from './SiteStatusCharts';
import DateTimeRangePicker from '../../utils/DateTimeRangePicker';
import CombPriceOptimSection from './CombPriceOptimCharts';
import CombEnergyAdeSection from './CombEnergyAdeCharts';
import CombLMPEnergyORCharts from './CombLMPEnergyORCharts';
import CombScheduleEnergyORCharts from './CombEnergyORCharts';
import { allowedIESOSystemIds, allowedNyisoSystemIds } from '../../apiConfig';

// ===== Date helpers (compute once) =====
const todayMidnight = dayjs().startOf('day');
const tomorrowMidnight = todayMidnight.add(1, 'day');
const thirtyDaysAgo = todayMidnight.subtract(30, 'day');

SiteStatus.propTypes = {
  msSessionInfo: PropTypes.object,
  site: PropTypes.object,
  bessData: PropTypes.object,
  isBessLoading: PropTypes.bool,
};

function SiteStatusTableSkeleton() {
  return (
    <Card>
      <TableContainer>
        <Table size="small" aria-label="site status table loading">
          <TableBody>
            {Array.from({ length: 3 }).map((_, index) => (
              <TableRow key={index}>
                <TableCell component="th" scope="row">
                  <Skeleton variant="text" width="65%" height={24} />
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Skeleton variant="text" width="45%" height={24} />
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
}

export default function SiteStatus({ msSessionInfo, site, bessData, isBessLoading = false }) {
  const [showStatusTable] = useState(true);

  const [startDate, setStartDate] = useState(todayMidnight);
  const [endDate, setEndDate] = useState(tomorrowMidnight);

  const [lastUpdatedTimestamp, setLastUpdatedTimestamp] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  const getLastUpdateTimeString = useCallback(
    () => `Last Updated ${dayjs().format('HH:mm:ss')}`,
    []
  );

  const bumpRefresh = useCallback(() => setRefreshKey((prev) => prev + 1), []);

  const handleRefresh = useCallback(() => {
    setLastUpdatedTimestamp(getLastUpdateTimeString());
    bumpRefresh();
  }, [bumpRefresh, getLastUpdateTimeString]);

  const handleRangeChange = useCallback(
    (range) => {
      const rawStart = range?.start ? dayjs(range.start) : startDate;
      const rawEnd = range?.end ? dayjs(range.end) : endDate;

      const sClamped = rawStart.isBefore(thirtyDaysAgo) ? thirtyDaysAgo : rawStart;
      let eClamped = rawEnd;

      if (!eClamped.isAfter(sClamped)) {
        eClamped = sClamped.add(1, 'minute');
      }

      if (sClamped.isSame(startDate) && eClamped.isSame(endDate)) return;

      setStartDate(sClamped);
      setEndDate(eClamped);
      setLastUpdatedTimestamp(getLastUpdateTimeString());
      bumpRefresh();
    },
    [startDate, endDate, bumpRefresh, getLastUpdateTimeString]
  );

  useEffect(() => {
    if (!site?.id || !msSessionInfo) return;
    setLastUpdatedTimestamp(getLastUpdateTimeString());
    bumpRefresh();
  }, [site?.id, msSessionInfo, bumpRefresh, getLastUpdateTimeString]);

  const { isIESOAllowed, isNyisoAllowed } = useMemo(() => {
    const id = site?.id;
    return {
      isIESOAllowed: !!id && allowedIESOSystemIds.has(id),
      isNyisoAllowed: !!id && allowedNyisoSystemIds.has(id),
    };
  }, [site?.id]);

  const hasBessData = Boolean(bessData && Object.keys(bessData).length > 0);

  const siteTableData = useMemo(
    () => (hasBessData ? generateSiteTableData(bessData) : []),
    [bessData]
  );

  const isModeUnknown =
    siteTableData[0]?.label === 'Current Mode' && siteTableData[0]?.value === 'Unknown';

  return (
    <Container maxWidth="xl">
      <Grid container spacing={3} direction="row" alignItems="stretch" justifyContent="center">
        {/* Status Table */}
        <Grid item xs={12} md={6} lg={6} order={{ xs: 2, sm: 2, md: 1, lg: 1 }}>
          {isBessLoading ? (
            <Box sx={{ mt: isModeUnknown ? 4 : 0 }}>
              <SiteStatusTableSkeleton />
            </Box>
          ) : hasBessData && showStatusTable ? (
            <Card sx={{ mt: isModeUnknown ? 4 : 0 }}>
              <TableContainer>
                <Table size="small" aria-label="site status table">
                  <TableBody>
                    {siteTableData.map((row) => {
                      if (row.label === 'Current Mode' && row.value === 'Unknown') return null;

                      return (
                        <TableRow
                          key={row.label}
                          sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        >
                          <TableCell component="th" scope="row">
                            {row.label}
                          </TableCell>
                          <TableCell align="right">{row.value}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          ) : (
            <Box sx={{ mt: 5 }}>
              <Typography variant="body2" sx={{ textAlign: 'center' }}>
                No status data available for this site.
              </Typography>
              <Typography
                variant="caption"
                sx={{ display: 'block', mt: 1, textAlign: 'center', color: 'text.secondary' }}
              >
                Select a different date/time range (data may exist on other days).
              </Typography>
            </Box>
          )}
        </Grid>

        {/* Date controls */}
        <Grid item xs={12} md={6} lg={6} order={{ xs: 1, sm: 1, md: 2, lg: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 1 }}>
              <Typography variant="caption" sx={{ mr: 1 }}>
                {lastUpdatedTimestamp}
              </Typography>
              <IconButton size="small" onClick={handleRefresh}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Box>

            <DateTimeRangePicker
              value={{
                start: startDate?.toDate(),
                end: endDate?.toDate(),
              }}
              onChange={handleRangeChange}
              style={{ width: '100%' }}
            />
          </Box>
        </Grid>

        {/* BESS Timeseries Chart */}
        <Grid item xs={12} md={12} lg={12} order={{ xs: 3, sm: 3, md: 3, lg: 3 }}>
          <SiteStatusCharts
            site={site}
            startDate={startDate}
            endDate={endDate}
            msSessionInfo={msSessionInfo}
            refreshKey={refreshKey}
          />
        </Grid>

        {/* Pricing + Optimization Charts (NYISO only) */}
        <Grid
          item
          xs={12}
          md={12}
          lg={12}
          order={{ xs: 4, sm: 4, md: 4, lg: 4 }}
          sx={{ mb: isNyisoAllowed ? 0 : 0.25 }}
        >
          {isNyisoAllowed ? (
            <CombPriceOptimSection
              site={site}
              startDate={startDate}
              endDate={endDate}
              msSessionInfo={msSessionInfo}
              refreshKey={refreshKey}
            />
          ) : (
            <Box sx={{ minHeight: 2 }} />
          )}
        </Grid>

        {/* LMP + OR Charts (IESO only) */}
        {isIESOAllowed && (
          <Grid item xs={12} md={12} lg={12} order={{ xs: 5, sm: 5, md: 5, lg: 5 }}>
            <CombLMPEnergyORCharts
              site={site}
              startDate={startDate}
              endDate={endDate}
              msSessionInfo={msSessionInfo}
              refreshKey={refreshKey}
            />
          </Grid>
        )}

        {/* Energy + OR Schedules (IESO only) */}
        {isIESOAllowed && (
          <Grid item xs={12} md={12} lg={12} order={{ xs: 6, sm: 6, md: 6, lg: 6 }}>
            <CombScheduleEnergyORCharts
              site={site}
              startDate={startDate}
              endDate={endDate}
              msSessionInfo={msSessionInfo}
              refreshKey={refreshKey}
            />
          </Grid>
        )}

        {/* Energy + Battery Capacity Charts (IESO only) */}
        {isIESOAllowed && (
          <Grid item xs={12} md={12} lg={12} order={{ xs: 7, sm: 7, md: 7, lg: 7 }}>
            <CombEnergyAdeSection
              site={site}
              startDate={startDate}
              endDate={endDate}
              msSessionInfo={msSessionInfo}
              refreshKey={refreshKey}
            />
          </Grid>
        )}
      </Grid>
    </Container>
  );
}